<script setup lang="ts">
// One panel of the report, as it reads in the Analysis pane: its title, what it
// shows, and its explainer behind an info control. The report's prose — what
// the panel measures, its denominators, how to read it beside its neighbours —
// was most of a panel's own height; a modal holds it now, one click away and
// out of the reading flow, so a question's panels fit a rail's width and the
// operator reads the chart they asked for rather than the essay around it.
import { ref } from "vue";

defineProps<{
	/** The panel's own title, stated on it. */
	title: string;
}>();

// The explainer's modal. It belongs to this panel, so two panels on screen
// never share one.
const helpOpen = ref(false);
</script>

<template>
	<section class="report-panel">
		<div class="report-panel-head">
			<h4 class="report-panel-title">{{ title }}</h4>
			<button
				v-if="$slots.help"
				type="button"
				class="report-panel-info"
				title="What this panel measures"
				aria-label="What this panel measures"
				@click="helpOpen = true"
			>
				<i class="bi bi-info-circle"></i>
			</button>
		</div>

		<div class="report-panel-body"><slot /></div>
	</section>

	<Teleport to="body">
		<div
			v-if="helpOpen"
			class="modal fade show d-block"
			tabindex="-1"
			@click.self="helpOpen = false"
		>
			<div class="modal-dialog modal-dialog-centered modal-dialog-scrollable">
				<div class="modal-content">
					<div class="modal-header">
						<h5 class="modal-title">
							<i class="bi bi-info-circle me-2"></i>{{ title }}
						</h5>
						<button
							type="button"
							class="btn-close"
							aria-label="Close"
							@click="helpOpen = false"
						></button>
					</div>
					<div class="modal-body report-help"><slot name="help" /></div>
				</div>
			</div>
		</div>
	</Teleport>
</template>

<style scoped>
.report-panel {
	display: flex;
	flex-direction: column;
	min-width: 0;
	background: #fff;
	border: 1px solid #dee2e6;
	border-radius: 4px;
	padding: 8px 10px 10px;
}

/* The head is the title and the explainer's control, so the control sits where
   the reader is already looking and never over the chart. */
.report-panel-head {
	display: flex;
	align-items: flex-start;
	gap: 6px;
	margin-bottom: 4px;
}

.report-panel-title {
	flex: 1 1 auto;
	min-width: 0;
	margin: 0;
	font-size: 0.8rem;
	font-weight: 600;
	color: #212529;
}

.report-panel-info {
	flex: 0 0 auto;
	border: 0;
	background: transparent;
	padding: 0 2px;
	line-height: 1;
	color: #6c757d;
	cursor: pointer;
}

.report-panel-info:hover {
	color: #0d6efd;
}

.report-panel-body {
	display: flex;
	flex-direction: column;
	min-width: 0;
	min-height: 0;
}

/* The explainer, read as prose rather than as a chart's footnote: the panel's
   own scale is 0.7rem, which is a caption size, and a modal is a reading
   surface. The paragraphs are the caller's markup, so they are reached through
   the scope boundary. */
.report-help :deep(p) {
	margin: 0 0 10px;
	font-size: 0.85rem;
	line-height: 1.5;
	color: #212529;
}

.report-help :deep(p:last-child) {
	margin-bottom: 0;
}
</style>
